import { fromAgorot, toAgorot } from './money';

export type PriceSource = 'list' | 'tier' | 'customer' | 'contract' | 'manual';

export interface ProductPricing {
  listPrice: number;
  tiers: { minQty: number; unitPrice: number }[];
}

export interface CustomerPriceRule {
  minQty: number;
  unitPrice: number | null;
  discountPct: number | null;
  isContract: boolean;
  validFrom: string | null; // YYYY-MM-DD
  validTo: string | null;
}

export interface ResolvedPrice {
  unitPrice: number;
  listPrice: number;
  source: PriceSource;
  /** Human-readable Hebrew explanation shown next to the price. */
  explanation: string;
}

function isActive(rule: CustomerPriceRule, onDate: string): boolean {
  return (!rule.validFrom || rule.validFrom <= onDate) && (!rule.validTo || rule.validTo >= onDate);
}

function bestByQty<T extends { minQty: number }>(rules: T[], qty: number): T | undefined {
  return rules.filter((r) => r.minQty <= qty).sort((a, b) => b.minQty - a.minQty)[0];
}

function applyRule(listPrice: number, rule: CustomerPriceRule): number {
  if (rule.unitPrice !== null) return rule.unitPrice;
  const pct = rule.discountPct ?? 0;
  return fromAgorot(Math.round((toAgorot(listPrice) * (100 - pct)) / 100));
}

/**
 * B2B price resolution (see DATABASE.md → Price resolution):
 * contract → customer-specific → quantity tier → list; org default discount applies to tier/list only.
 */
export function resolvePrice(params: {
  product: ProductPricing;
  customerRules: CustomerPriceRule[];
  orgDefaultDiscountPct: number | null;
  quantity: number;
  onDate: string; // YYYY-MM-DD (Israel)
}): ResolvedPrice {
  const { product, quantity, onDate } = params;
  const active = params.customerRules.filter((r) => isActive(r, onDate));

  const contract = bestByQty(active.filter((r) => r.isContract), quantity);
  if (contract) {
    return {
      unitPrice: applyRule(product.listPrice, contract),
      listPrice: product.listPrice,
      source: 'contract',
      explanation: contract.unitPrice !== null ? 'מחיר חוזה' : `חוזה: ${contract.discountPct}% הנחה ממחירון`,
    };
  }
  const custom = bestByQty(active.filter((r) => !r.isContract), quantity);
  if (custom) {
    return {
      unitPrice: applyRule(product.listPrice, custom),
      listPrice: product.listPrice,
      source: 'customer',
      explanation: custom.unitPrice !== null ? 'מחיר מוסכם ללקוח' : `${custom.discountPct}% הנחה מוסכמת`,
    };
  }
  const tier = bestByQty(product.tiers, quantity);
  const base = tier ? tier.unitPrice : product.listPrice;
  const pct = params.orgDefaultDiscountPct ?? 0;
  const unitPrice = pct > 0 ? fromAgorot(Math.round((toAgorot(base) * (100 - pct)) / 100)) : base;
  const parts: string[] = [tier ? `מדרגת כמות (${tier.minQty}+)` : 'מחירון'];
  if (pct > 0) parts.push(`${pct}% הנחת לקוח`);
  return { unitPrice, listPrice: product.listPrice, source: tier ? 'tier' : 'list', explanation: parts.join(' + ') };
}

export const PRICE_SOURCE_LABELS: Record<PriceSource, string> = {
  list: 'מחירון',
  tier: 'מדרגת כמות',
  customer: 'מחיר לקוח',
  contract: 'מחיר חוזה',
  manual: 'ידני',
};
